import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  CampaignPager as Pager,
  campaignDate as date,
} from "../components/CampaignUi";
import {
  getAdminNotificationCommunities,
  getAdminNotificationEvents,
  useApiClient,
} from "../api/client";
import type {
  AdminNotificationCommunityOption,
  AdminNotificationEventOption,
} from "../api/client";
import {
  campaignApi,
  Audience,
  Campaign,
  CampaignPayload,
  CampaignRecipient,
} from "../api/campaigns";
import { CampaignEditor, emptyContent } from "../components/CampaignEditor";
import { CommunitySelect } from "../components/CommunitySelect";
import { CampaignFilters } from "../components/CampaignFilters";
import { NotificationUserSelect } from "../components/NotificationUserSelect";
import type { UserSearchResult } from "../components/UserTypeahead";
import { PageHeader, PageShell, StatusBadge } from "../components/ui";
import { useToast } from "../toast/ToastProvider";
import { formatDateTime } from "../utils/dateFormat";

const initial = (): CampaignPayload => ({
  channel: "EMAIL",
  campaignName: "",
  subject: "",
  title: "",
  body: "",
  content: emptyContent,
  userGroup: "ALL",
  communityId: null,
  eventId: null,
  surveyId: null,
  userIds: [],
  screen: null,
  actionLabel: "",
  actionUrl: "",
  filters: {},
});
const inputClass = "form-control mt-1.5 w-full font-normal";
const buttonClass = "btn btn-neutral focus-visible:outline-secondary";
export const CampaignEditorPage: React.FC = () => {
  const { request } = useApiClient();
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const [loadError, setLoadError] = useState("");
  const api = useMemo(() => campaignApi(request), [request]);
  const { showError, showSuccess } = useToast();
  const [form, setForm] = useState<CampaignPayload>(initial);
  const [selected, setSelected] = useState<Campaign | null>(null);
  const [communities, setCommunities] = useState<
    AdminNotificationCommunityOption[]
  >([]);
  const [events, setEvents] = useState<AdminNotificationEventOption[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<UserSearchResult[]>([]);
  const leadAudience = form.userGroup === "LEADS" || form.userGroup === "SURVEY_LEADS";
  const [surveys, setSurveys] = useState<Array<{ id: string; title: string; code: string | null }>>([]);
  const [surveysLoading, setSurveysLoading] = useState(false);
  const [surveysError, setSurveysError] = useState("");
  const audienceReady = form.userGroup !== "SURVEY_LEADS" || (!!form.communityId && !!form.surveyId && !surveysLoading && !surveysError);
  const [audience, setAudience] = useState<Audience | null>(null);
  const [audiencePage, setAudiencePage] = useState(0);
  const [html, setHtml] = useState("");
  const [previewError, setPreviewError] = useState("");
  const [busy, setBusy] = useState(false);
  const [adminId, setAdminId] = useState("");
  const [panel, setPanel] = useState<"content" | "audience">("content");
  const [mobilePreview, setMobilePreview] = useState(false);
  const audienceKey = JSON.stringify([
    form.channel,
    form.userGroup,
    form.communityId,
    form.eventId,
    form.surveyId,
    form.userIds,
    form.filters,
  ]);
  const filterCount = Object.values(form.filters).filter(
    (v) => v != null && v !== "" && v !== "any"
  ).length;
  const readOnly = !!selected && !selected.draft;
  const report = useCallback(
    (e: unknown) => showError((e as Error)?.message || "Aktion fehlgeschlagen"),
    [showError]
  );

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [campaignId]);
  useEffect(() => {
    if (!campaignId || selected?.id === campaignId) return;
    let cancelled = false;
    setLoadError("");
    api
      .get(campaignId)
      .then((c) => {
        if (cancelled) return;
        if (!c.draft) {
          navigate(`/notifications/${encodeURIComponent(c.id)}`, {
            replace: true,
          });
          return;
        }
        setSelected(c);
        setForm({
          ...c,
          filters: c.filters || {},
          content: c.content || emptyContent,
          campaignName: c.campaignName || "",
          subject: c.subject || "",
          actionLabel: c.actionLabel || "",
          actionUrl: c.actionUrl || "",
        });
        setSelectedUsers(
          (c.userIds || []).map((id) => ({ id, email: id } as UserSearchResult))
        );
      })
      .catch((e) => {
        if (!cancelled)
          setLoadError(
            (e as Error).message || "Kampagne konnte nicht geladen werden."
          );
      });
    return () => {
      cancelled = true;
    };
  }, [api, campaignId, navigate, selected?.id]);
  useEffect(() => {
    let cancelled = false;
    Promise.all([
      getAdminNotificationCommunities(request),
      getAdminNotificationEvents(request),
      request<{ id: string }>("/me"),
    ])
      .then(([cs, es, me]) => {
        if (!cancelled) {
          setCommunities(cs);
          setEvents(es.content);
          setAdminId(me.id);
        }
      })
      .catch(report);
    return () => {
      cancelled = true;
    };
  }, [request, report]);
  useEffect(() => {
    let cancelled = false;
    setSurveys([]);
    setSurveysError("");
    if (form.userGroup !== "SURVEY_LEADS" || !form.communityId) {
      setSurveysLoading(false);
      return;
    }
    setSurveysLoading(true);
    const communityId = form.communityId;
    async function load() {
      const options: Array<{ id: string; title: string; code: string | null }> = [];
      for (let page = 0; ; page++) {
        const result = await api.surveys(communityId, page);
        if (cancelled) return;
        options.push(...result.content);
        if (result.last) break;
      }
      if (!cancelled) setSurveys(options);
    }
    void load().catch((e) => {
      if (!cancelled) setSurveysError((e as Error).message || "Umfragen konnten nicht geladen werden.");
    }).finally(() => { if (!cancelled) setSurveysLoading(false); });
    return () => { cancelled = true; };
  }, [api, form.userGroup, form.communityId]);
  useEffect(() => {
    let cancelled = false;
    if (campaignId && selected?.id !== campaignId) return;
    const timer = window.setTimeout(() => {
      if (form.channel !== "EMAIL" || !audienceReady) {
        setHtml("");
        setPreviewError("");
        return;
      }
      api
        .preview(form)
        .then((result) => {
          if (!cancelled) {
            setHtml(result.html);
            setPreviewError("");
          }
        })
        .catch((e) => {
          if (!cancelled) {
            setHtml("");
            setPreviewError((e as Error).message);
          }
        });
    }, 400);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [api, form, campaignId, selected?.id, audienceReady]);
  useEffect(() => {
    setAudience(null);
    setAudiencePage(0);
  }, [audienceKey]);
  function change<K extends keyof CampaignPayload>(
    key: K,
    value: CampaignPayload[K]
  ) {
    setForm((current) => ({ ...current, [key]: value }));
  }
  async function save() {
    const c = await api.save(form, selected?.id);
    setSelected(c);
    navigate(`/notifications/${encodeURIComponent(c.id)}/edit`, {
      replace: true,
    });
    return c;
  }
  async function action(run: () => Promise<void>) {
    setBusy(true);
    try {
      await run();
    } catch (e) {
      report(e);
    } finally {
      setBusy(false);
    }
  }
  async function checkAudience(page = 0) {
    const result = await api.audience(form, page);
    setAudience(result);
    setAudiencePage(page);
    return result;
  }
  async function send() {
    const preview = await checkAudience();
    if (!preview.eligible) {
      showError("Keine versandberechtigten Empfänger.");
      return;
    }
    if (
      !window.confirm(
        `Jetzt an ${preview.eligible} Empfänger senden? ${preview.excluded} Empfänger werden ausgeschlossen.`
      )
    )
      return;
    const c = await save();
    const sent = await api.send(c.id);
    navigate(`/notifications/${encodeURIComponent(sent.id)}`, {
      replace: true,
    });
    showSuccess(
      "Versand gestartet. Den Fortschritt findest du in den Analytics."
    );
  }
  if (campaignId && selected?.id !== campaignId)
    return (
      <PageShell>
        <Link className="btn btn-ghost mb-4" to="/notifications">
          ← Alle Kampagnen
        </Link>
        {loadError ? (
          <p
            role="alert"
            className="rounded-xl border border-red-200 bg-red-50 p-5 text-red-700"
          >
            {loadError}
          </p>
        ) : (
          <p role="status">Kampagne wird geladen …</p>
        )}
      </PageShell>
    );

  return (
    <PageShell>
      <Link
        className="mb-5 inline-flex items-center gap-2 text-sm font-medium text-gray-500 hover:text-secondary"
        to="/notifications"
      >
        ← Alle Kampagnen
      </Link>
      <PageHeader
        title={selected ? "Entwurf bearbeiten" : "Neue Kampagne"}
        description="Gestalte deine Nachricht und wähle, wen du erreichen möchtest."
      />
      <div className="rounded-2xl border border-gray-200 bg-white shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-4 border-b border-gray-100 px-5 py-4 sm:px-7">
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/20 text-gray-900">
              <CampaignIcon name={form.channel === "EMAIL" ? "mail" : "push"} />
            </span>
            <div>
              <h2 className="font-semibold text-gray-900">
                {readOnly
                  ? "Gesendete Kampagne"
                  : selected
                  ? "Entwurf bearbeiten"
                  : "Neue Kampagne"}
              </h2>
              <p className="mt-0.5 text-xs text-gray-500">
                {readOnly
                  ? date(selected?.sentAt || null)
                  : "Erstellen, Empfänger prüfen und persönlich erreichen."}
              </p>
            </div>
          </div>
          <StatusBadge tone={readOnly ? "green" : "amber"}>
            {readOnly ? "Versand gestartet" : "Entwurf"}
          </StatusBadge>
        </div>
        <div className="grid xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
          <div className="min-w-0">
            <nav
              aria-label="Kampagne bearbeiten"
              className="flex gap-1 border-b border-gray-100 px-5 pt-3 sm:px-7"
            >
              {(
                [
                  ["content", "Inhalt"],
                  ["audience", "Zielgruppe & Empfänger"],
                ] as const
              ).map(([key, label], index) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setPanel(key)}
                  aria-pressed={panel === key}
                  className={`flex items-center gap-2 border-b-2 px-3 pb-3 pt-2 text-sm font-semibold transition-colors focus-visible:outline-secondary ${
                    panel === key
                      ? "border-secondary text-secondary"
                      : "border-transparent text-gray-500 hover:text-gray-900"
                  }`}
                >
                  <span
                    aria-hidden="true"
                    className={`flex h-5 w-5 items-center justify-center rounded-full text-xs ${
                      panel === key ? "bg-secondary/10" : "bg-gray-100"
                    }`}
                  >
                    {index + 1}
                  </span>
                  {label}
                  {key === "audience" && filterCount > 0 && (
                    <span className="rounded-full bg-secondary/10 px-1.5 text-xs">
                      {filterCount}
                    </span>
                  )}
                </button>
              ))}
            </nav>
            <fieldset
              disabled={busy || readOnly}
              className="min-w-0 disabled:opacity-75"
            >
              <div
                hidden={panel !== "content"}
                className="space-y-6 p-5 sm:p-7"
              >
                <div>
                  <p className="mb-2 text-sm font-medium text-gray-700">
                    Kanal
                  </p>
                  <div
                    className="inline-flex rounded-xl bg-gray-100 p-1"
                    role="group"
                    aria-label="Kanal"
                  >
                    {(
                      [
                        ["EMAIL", "E-Mail", "mail"],
                        ["PUSH", "Push", "push"],
                      ] as const
                    ).map(([value, label, icon]) => (
                      <button
                        key={value}
                        type="button"
                        aria-pressed={form.channel === value}
                        onClick={() => change("channel", value)}
                        disabled={leadAudience && value === "PUSH"}
                        className={`flex items-center gap-2 rounded-lg px-5 py-2 text-sm font-semibold transition focus-visible:outline-secondary ${
                          form.channel === value
                            ? "bg-white text-gray-900 shadow-sm"
                            : "text-gray-500 hover:text-gray-900"
                        }`}
                      >
                        <CampaignIcon name={icon} />
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
                <label className="block text-sm font-medium text-gray-700">
                  Interner Kampagnenname
                  <input
                    className={inputClass}
                    maxLength={255}
                    aria-label="Interner Kampagnenname"
                    placeholder="z. B. Gemeinsam in den Herbst"
                    value={form.campaignName}
                    onChange={(e) => change("campaignName", e.target.value)}
                  />
                  <span className="mt-1.5 block text-xs font-normal text-gray-500">
                    Nur für euch sichtbar.
                  </span>
                </label>
                <div className="space-y-5 border-t border-gray-100 pt-6">
                  <div>
                    <h3 className="font-semibold text-gray-900">
                      Deine Nachricht
                    </h3>
                    <p className="mt-1 text-xs text-gray-500">
                      {form.channel === "EMAIL"
                        ? "Euer mobie-Design steht. Du bringst den Inhalt."
                        : "Kurz, persönlich und direkt aufs Smartphone."}
                    </p>
                  </div>
                  {form.channel === "EMAIL" && (
                    <label className="block text-sm font-medium text-gray-700">
                      Betreff
                      <input
                        className={inputClass}
                        maxLength={255}
                        placeholder="Was gibt’s Neues bei mobie?"
                        value={form.subject}
                        onChange={(e) => change("subject", e.target.value)}
                      />
                    </label>
                  )}
                  <label className="block text-sm font-medium text-gray-700">
                    {form.channel === "EMAIL" ? "Überschrift" : "Titel"}
                    <input
                      className={inputClass}
                      maxLength={255}
                      placeholder="Deine nächste Fahrt wartet"
                      value={form.title}
                      onChange={(e) => change("title", e.target.value)}
                    />
                  </label>
                  {form.channel === "EMAIL" ? (
                    <div>
                      <p className="mb-2 text-sm font-medium text-gray-700">
                        E-Mail-Inhalt
                      </p>
                      <CampaignEditor
                        value={form.content || emptyContent}
                        disabled={busy || readOnly}
                        onChange={(content) => change("content", content)}
                      />
                      <p className="mt-2 text-xs leading-relaxed text-gray-500">
                        Bis zu 4000 Textzeichen. Persönliche Anrede und
                        Abmeldelink ergänzen wir automatisch.
                      </p>
                    </div>
                  ) : (
                    <label className="block text-sm font-medium text-gray-700">
                      Text
                      <textarea
                        className={inputClass}
                        rows={6}
                        maxLength={4000}
                        placeholder="Deine Nachricht an die Community …"
                        value={form.body}
                        onChange={(e) => change("body", e.target.value)}
                      />
                    </label>
                  )}
                </div>
                {form.channel === "EMAIL" ? (
                  <div className="rounded-xl border border-gray-200 bg-gray-50/60 p-4">
                    <div className="mb-4 flex items-center gap-2">
                      <h3 className="text-sm font-semibold text-gray-900">
                        Button hinzufügen
                      </h3>
                      <span className="text-xs text-gray-500">Optional</span>
                    </div>
                    <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-1 2xl:grid-cols-2">
                      <label className="text-sm font-medium text-gray-700">
                        Button-Text (optional)
                        <input
                          className={inputClass}
                          maxLength={255}
                          placeholder="Fahrt finden"
                          value={form.actionLabel}
                          onChange={(e) =>
                            change("actionLabel", e.target.value)
                          }
                        />
                      </label>
                      <label className="text-sm font-medium text-gray-700">
                        Button-Ziel (HTTPS)
                        <input
                          className={inputClass}
                          maxLength={2048}
                          type="url"
                          placeholder="https://…"
                          value={form.actionUrl}
                          onChange={(e) => change("actionUrl", e.target.value)}
                        />
                      </label>
                    </div>
                  </div>
                ) : (
                  <label className="block text-sm font-medium text-gray-700">
                    Beim Öffnen anzeigen
                    <select
                      className={inputClass}
                      value={form.screen || ""}
                      disabled={form.userGroup === "EVENT"}
                      onChange={(e) => change("screen", e.target.value || null)}
                    >
                      <option value="">App-Startseite</option>
                      {Object.entries({
                        HomeScreen: "Startseite",
                        RecurringRideScreen: "Regelmäßige Fahrten",
                        VerificationScreen: "Verifizierung",
                        PaymentScreen: "Zahlungen",
                        HomeScreenRideMode: "Fahrt suchen",
                        HomeScreenDriveMode: "Fahrt anbieten",
                        SavedLocationsScreen: "Gespeicherte Orte",
                        eventDetail: "Eventdetails",
                      }).map(([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </select>
                  </label>
                )}
              </div>
              <div
                hidden={panel !== "audience"}
                className="space-y-6 p-5 sm:p-7"
              >
                <div>
                  <h3 className="font-semibold text-gray-900">
                    Wen möchtest du erreichen?
                  </h3>
                  <p className="mt-1 text-sm leading-relaxed text-gray-500">
                    Zielgruppe wählen, Filter kombinieren und die konkreten
                    Empfänger vor dem Versand ansehen.
                  </p>
                </div>
                <label className="block text-sm font-medium text-gray-700">
                  Zielgruppe
                  <select
                    className={inputClass}
                    value={form.userGroup}
                    onChange={(e) => {
                      setSelectedUsers([]);
                      setForm((f) => ({
                        ...f,
                        userGroup: e.target
                          .value as CampaignPayload["userGroup"],
                        communityId: null,
                        eventId: null,
                        userIds: [],
                        surveyId: null,
                        filters: e.target.value === "LEADS" || e.target.value === "SURVEY_LEADS" || leadAudience ? {} : f.filters,
                        channel: e.target.value === "LEADS" || e.target.value === "SURVEY_LEADS" ? "EMAIL" : f.channel,
                        screen:
                          e.target.value === "EVENT" ? "eventDetail" : null,
                      }));
                    }}
                  >
                    <option value="ALL">Alle Nutzer</option>
                    <option value="COMMUNITY">Community</option>
                    <option value="EVENT">Event</option>
                    <option value="USERS">Ausgewählte Nutzer</option>
                    <option value="LEADS">Leads ohne Umfrage</option>
                    <option value="SURVEY_LEADS">Teilnehmer einer Umfrage</option>
                  </select>
                </label>
                {leadAudience && (
                  <p className="rounded-lg bg-secondary/5 p-3 text-sm text-gray-600">
                    {form.userGroup === "LEADS"
                      ? "Nur Leads ohne Umfrage. Bestehende mobie-Nutzer werden ausgeschlossen."
                      : "Nur Teilnehmer der gewählten Umfrage, auch mit bestehendem mobie-Konto."}
                  </p>
                )}
                {form.userGroup === "SURVEY_LEADS" && (
                  <>
                    <CommunitySelect communities={communities} label="Gemeinde / Community" placeholder="Gemeinde auswählen"
                      value={form.communityId || ""} onChange={(id) => setForm(f => ({ ...f, communityId: id || null, surveyId: null }))} />
                    <label className="block text-sm font-medium text-gray-700">
                      Umfrage
                      <select className={inputClass} value={form.surveyId || ""}
                        disabled={!form.communityId || surveysLoading || !!surveysError}
                        onChange={(e) => change("surveyId", e.target.value || null)}>
                        <option value="">{surveysLoading ? "Umfragen werden geladen …" : "Umfrage auswählen"}</option>
                        {surveys.map(s => <option key={s.id} value={s.id}>{s.title}{s.code ? ` · ${s.code}` : ""}</option>)}
                      </select>
                    </label>
                    {surveysError && <p role="alert" className="text-sm text-red-700">{surveysError}</p>}
                    {form.communityId && !surveysLoading && !surveysError && !surveys.length &&
                      <p className="text-sm text-gray-500">Für diese Gemeinde gibt es noch keine veröffentlichten Umfragen.</p>}
                  </>
                )}
                {form.userGroup === "COMMUNITY" && (
                  <CommunitySelect
                    communities={communities}
                    label="Community-Zielgruppe"
                    placeholder="Community auswählen"
                    value={form.communityId || ""}
                    onChange={(id) => change("communityId", id || null)}
                  />
                )}
                {form.userGroup === "EVENT" && (
                  <label className="block text-sm font-medium text-gray-700">
                    Event
                    <select
                      className={inputClass}
                      value={form.eventId || ""}
                      onChange={(e) =>
                        change("eventId", e.target.value || null)
                      }
                    >
                      <option value="">Event auswählen</option>
                      {events.map((e) => (
                        <option key={e.id} value={e.id}>
                          {e.name} ·{" "}
                          {e.startTime ? formatDateTime(e.startTime) : ""}
                        </option>
                      ))}
                    </select>
                  </label>
                )}
                {form.userGroup === "USERS" && (
                  <NotificationUserSelect
                    selectedUsers={selectedUsers}
                    onSelect={(u) => {
                      if (form.userIds.includes(u.id)) return;
                      setSelectedUsers((us) => [...us, u]);
                      change("userIds", [...form.userIds, u.id]);
                    }}
                    onRemove={(id) => {
                      setSelectedUsers((us) => us.filter((u) => u.id !== id));
                      change(
                        "userIds",
                        form.userIds.filter((uid) => uid !== id)
                      );
                    }}
                  />
                )}
                {!leadAudience && <div className="border-t border-gray-100 pt-5">
                  <CampaignFilters
                    communities={communities}
                    value={form.filters}
                    onChange={(filters) => change("filters", filters)}
                  />
                </div>}
              </div>
            </fieldset>
            {!readOnly && (
              <section
                hidden={panel !== "audience"}
                aria-label="Empfängervorschau"
                className="mx-5 mb-7 overflow-hidden rounded-xl border border-secondary/20 sm:mx-7"
              >
                <div className="space-y-3 bg-secondary/5 p-4">
                  <div className="flex items-center gap-2 text-secondary">
                    <CampaignIcon name="users" />
                    <h3 className="text-sm font-semibold">Empfängervorschau</h3>
                  </div>
                  <p className="text-xs leading-relaxed text-gray-600">
                    Hier siehst du, wer die Nachricht erhalten kann und wer
                    ausgeschlossen wird. Es wird noch nichts versendet.
                  </p>
                  <button
                    className="btn btn-neutral w-full border-secondary/20 text-secondary hover:bg-secondary/5"
                    disabled={busy || !audienceReady}
                    onClick={() =>
                      void action(async () => {
                        await checkAudience();
                      })
                    }
                  >
                    {busy
                      ? "Bitte warten …"
                      : audience
                      ? "Empfänger aktualisieren"
                      : "Empfänger anzeigen"}
                    <CampaignIcon name="arrow" />
                  </button>
                </div>
                {audience ? (
                  <div className="p-4" aria-live="polite">
                    <div className="mb-4 grid grid-cols-3 gap-2">
                      {[
                        ["Treffer", audience.targeted],
                        ["Versandberechtigt", audience.eligible],
                        ["Ausgeschlossen", audience.excluded],
                      ].map(([label, value]) => (
                        <div key={label}>
                          <p className="text-2xl font-semibold tracking-tight text-gray-900">
                            {value}
                          </p>
                          <p className="mt-1 break-words text-xs text-gray-500">
                            {label}
                          </p>
                        </div>
                      ))}
                    </div>
                    <AudienceList rows={audience.recipients.content} />
                    <Pager
                      page={audiencePage}
                      last={audience.recipients.last}
                      disabled={busy}
                      onPage={(p) =>
                        void action(async () => {
                          await checkAudience(p);
                        })
                      }
                    />
                  </div>
                ) : (
                  <p className="px-4 py-5 text-center text-xs text-gray-500">
                    Noch keine Vorschau für diese Filter geladen.
                  </p>
                )}
              </section>
            )}
          </div>
          <aside className="min-w-0 border-t border-gray-200 bg-[#f3f2f0] xl:border-l xl:border-t-0">
            <div className="sticky top-20 p-5 sm:p-7">
              <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <span className="h-2 w-2 rounded-full bg-secondary" />
                  <h3 className="text-sm font-semibold text-gray-700">
                    Live-Vorschau
                  </h3>
                </div>
                {form.channel === "EMAIL" && (
                  <div
                    className="flex rounded-lg border border-gray-200 bg-white p-1"
                    role="group"
                    aria-label="Vorschaugröße"
                  >
                    {(
                      [
                        [false, "Desktop", "desktop"],
                        [true, "Mobil", "mobile"],
                      ] as const
                    ).map(([mobile, label, icon]) => (
                      <button
                        key={label}
                        type="button"
                        title={label}
                        aria-label={label}
                        aria-pressed={mobilePreview === mobile}
                        onClick={() => setMobilePreview(mobile)}
                        className={`rounded-md px-2.5 py-1.5 ${
                          mobilePreview === mobile
                            ? "bg-gray-100 text-gray-900"
                            : "text-gray-400 hover:text-gray-700"
                        }`}
                      >
                        <CampaignIcon name={icon} />
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {previewError && (
                <p
                  role="alert"
                  className="mb-3 rounded-lg bg-red-50 p-3 text-sm text-red-700"
                >
                  {previewError}
                </p>
              )}
              {form.channel === "EMAIL" ? (
                <div
                  className={`mx-auto overflow-hidden rounded-xl border border-gray-200 bg-white shadow-[0_10px_35px_-15px_rgba(0,0,0,0.2)] transition-[max-width] ${
                    mobilePreview ? "max-w-[375px]" : "max-w-full"
                  }`}
                >
                  <div className="border-b border-gray-100 px-5 py-4">
                    <div
                      className="mb-3 flex items-center gap-1.5"
                      aria-hidden="true"
                    >
                      <span className="h-2 w-2 rounded-full bg-gray-200" />
                      <span className="h-2 w-2 rounded-full bg-gray-200" />
                      <span className="h-2 w-2 rounded-full bg-gray-200" />
                    </div>
                    <p className="break-words text-sm font-semibold text-gray-900">
                      {form.subject || "Dein Betreff erscheint hier"}
                    </p>
                    <p className="mt-1 text-xs text-gray-500">
                      mobie <span className="mx-1 text-gray-300">→</span> Deine
                      Empfänger
                    </p>
                  </div>
                  <iframe
                    title="Mailvorschau"
                    sandbox=""
                    srcDoc={html}
                    className="h-[580px] w-full bg-white"
                  />
                </div>
              ) : (
                <div className="mx-auto max-w-[330px] rounded-[2.5rem] border-[7px] border-gray-800 bg-gradient-to-b from-violet-100 to-amber-50 px-4 pb-32 pt-10 shadow-lg">
                  <p className="text-center text-xs font-medium text-gray-500">
                    {new Intl.DateTimeFormat("de-AT", {
                      weekday: "long",
                      day: "numeric",
                      month: "long",
                    }).format(new Date())}
                  </p>
                  <p className="mb-8 mt-2 text-center text-5xl font-light text-gray-700">
                    09:41
                  </p>
                  <div className="rounded-2xl border border-white/80 bg-white/80 p-4 shadow-sm">
                    <div className="mb-2 flex items-center gap-2 text-xs text-gray-600">
                      <span className="rounded bg-primary px-1.5 py-1 font-bold text-gray-900">
                        m
                      </span>
                      mobie<span className="ml-auto text-gray-400">jetzt</span>
                    </div>
                    <strong className="break-words text-sm text-gray-900">
                      {form.title || "Dein Titel"}
                    </strong>
                    <p className="mt-1 whitespace-pre-line break-words text-sm text-gray-600">
                      {form.body || "Deine Nachricht erscheint hier."}
                    </p>
                  </div>
                </div>
              )}
              <p className="mt-4 text-center text-xs leading-relaxed text-gray-500">
                {form.channel === "EMAIL"
                  ? "Original-Mailtemplate · Anrede wird pro Empfänger personalisiert"
                  : "Beispielansicht · Darstellung variiert je nach Gerät"}
              </p>
            </div>
          </aside>
        </div>
        {!readOnly && (
          <div className="sticky bottom-0 z-[5] flex flex-wrap items-center justify-between gap-4 rounded-b-2xl border-t border-gray-200 bg-white/95 px-5 py-4 backdrop-blur sm:px-7">
            <button
              type="button"
              onClick={() => setPanel("audience")}
              className="flex items-center gap-2 text-sm text-gray-600 hover:text-secondary"
            >
              <CampaignIcon name="users" />
              {audience
                ? `${audience.eligible} versandberechtigte Empfänger`
                : "Empfänger noch nicht geprüft"}
              <CampaignIcon name="arrow" />
            </button>
            <div className="flex flex-wrap gap-2">
              <button
                className="btn btn-ghost"
                disabled={busy}
                onClick={() =>
                  void action(async () => {
                    await save();
                    showSuccess("Entwurf gespeichert.");
                  })
                }
              >
                Entwurf speichern
              </button>
              <button
                className={buttonClass}
                disabled={busy || !adminId}
                onClick={() =>
                  void action(async () => {
                    const c = await save();
                    await api.test(c.id, adminId);
                    showSuccess("Testversand an dein Admin-Konto gestartet.");
                  })
                }
              >
                {form.channel === "EMAIL"
                  ? "Testmail an mich"
                  : "Test-Push an mich"}
              </button>
              <button
                className="btn border-primary bg-primary text-gray-900 hover:bg-amber-400 focus-visible:outline-secondary"
                disabled={busy || !audienceReady}
                onClick={() => void action(send)}
              >
                {busy ? "Bitte warten …" : "Jetzt senden"}
                <CampaignIcon name="arrow" />
              </button>
            </div>
          </div>
        )}
      </div>
    </PageShell>
  );
};
function AudienceList({ rows }: { rows: CampaignRecipient[] }) {
  const reasons: Record<string, string> = {
    USER_DELETED: "Nutzer gelöscht",
    EMAIL_OPT_OUT: "Von optionalen E-Mails abgemeldet",
    OPTED_OUT: "Benachrichtigungen deaktiviert",
    NO_DESTINATION: "Keine erreichbare Adresse / kein Gerät",
    ALREADY_REGISTERED: "Bereits bei mobie registriert",
    PROVIDER_GLOBAL_SUPPRESSION: "Adresse für den Versand gesperrt",
  };
  return (
    <ul className="divide-y divide-gray-100">
      {rows.map((r) => (
        <li
          key={r.recipientId || r.userId || r.leadId || r.email}
          className="flex flex-wrap items-center justify-between gap-2 py-3"
        >
          <div className="min-w-0 flex-1">
            {r.userId ? <Link
              to={`/users/${encodeURIComponent(r.userId)}`}
              target="_blank"
              rel="noreferrer"
              className="block truncate text-sm font-semibold text-gray-900 hover:text-secondary hover:underline"
            >
              {r.name || r.email || "Nutzerprofil"}
              <span className="sr-only"> (öffnet in neuem Tab)</span>
            </Link> : <p className="truncate text-sm font-semibold text-gray-900">{r.name || r.email}</p>}
            <p className="truncate text-xs text-gray-500">
              {r.email || "Keine E-Mail-Adresse"}
            </p>
            {r.reason && (
              <p className="mt-1 text-xs text-amber-800">
                {reasons[r.reason] || r.reason}
              </p>
            )}
          </div>
          <StatusBadge tone={r.reason ? "amber" : "green"}>
            {r.reason ? "Ausgeschlossen" : "Versandberechtigt"}
          </StatusBadge>
        </li>
      ))}
      {!rows.length && (
        <li className="py-6 text-center text-sm text-gray-500">
          Keine Empfänger für diese Auswahl gefunden.
        </li>
      )}
    </ul>
  );
}
const campaignIcons = {
  mail: "M3 5h18v14H3V5zm0 1 9 7 9-7",
  push: "M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9M10 21h4",
  users:
    "M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2m20 0v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75M13 7a4 4 0 1 1-8 0 4 4 0 0 1 8 0",
  arrow: "M5 12h14m-6-6 6 6-6 6",
  refresh:
    "M20 7v5h-5M4 17v-5h5m-4-4a8 8 0 0 1 13-3l2 3M4 16l2 3a8 8 0 0 0 13-3",
  desktop: "M3 3h18v13H3V3zm5 18h8m-4-5v5",
  mobile: "M7 2h10v20H7V2zm4 17h2",
};
function CampaignIcon({ name }: { name: keyof typeof campaignIcons }) {
  return (
    <svg
      aria-hidden="true"
      className="h-4 w-4 shrink-0"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d={campaignIcons[name]} />
    </svg>
  );
}
